import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiPost } from "../../lib/api";

type TelegramSystemMiniAppLoginResponse = {
  access_token: string;
  token_type: "bearer";
  expires_in: number;
  redirect_path: string;
  source: "telegram_mini_app";
};

type TelegramWebAppHandle = {
  initData?: string;
  ready?: () => void;
  expand?: () => void;
};

function getTelegramWebApp(): TelegramWebAppHandle | null {
  const maybeTelegram = (window as unknown as { Telegram?: { WebApp?: TelegramWebAppHandle } }).Telegram;
  return maybeTelegram?.WebApp ?? null;
}

function sleep(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function getLaunchParamFromHash(key: string): string {
  const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash;
  if (!hash) return "";
  const params = new URLSearchParams(hash);
  return params.get(key) || "";
}

function getTelegramInitData(searchParams: URLSearchParams): string {
  const webApp = getTelegramWebApp();
  return (
    webApp?.initData ||
    searchParams.get("init_data") ||
    searchParams.get("tgWebAppData") ||
    getLaunchParamFromHash("tgWebAppData") ||
    ""
  );
}

function getStartParam(searchParams: URLSearchParams): string | undefined {
  return (
    searchParams.get("startapp") ||
    searchParams.get("tgWebAppStartParam") ||
    getLaunchParamFromHash("tgWebAppStartParam") ||
    undefined
  );
}

export function TelegramSystemEntryPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("Preparing Telegram system manager session...");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      for (let i = 0; i < 10; i += 1) {
        const telegramWebApp = getTelegramWebApp();
        try {
          telegramWebApp?.ready?.();
          telegramWebApp?.expand?.();
        } catch {
          // Non-Telegram browsers may not support these calls.
        }

        const initData = getTelegramInitData(searchParams);
        if (initData) {
          setError("");
          setStatus("Signing in to system console via Telegram...");
          try {
            const data = await apiPost<TelegramSystemMiniAppLoginResponse>(
              "/v3/sys/auth/telegram-miniapp/login",
              {
                init_data: initData,
                start_param_override: getStartParam(searchParams),
              },
              { auth: "none" }
            );
            if (cancelled) return;
            localStorage.setItem("v3_platform_token", data.access_token);
            setStatus("Redirecting to system workspace...");
            window.location.assign(data.redirect_path || "/tg/system/dashboard");
            return;
          } catch (err) {
            if (cancelled) return;
            setError(err instanceof Error ? err.message : "Telegram sign-in failed.");
            setStatus("Unable to start Telegram system manager mode.");
            return;
          }
        }

        await sleep(150);
        if (cancelled) return;
      }

      setError("Telegram initData is missing. Open this page from the Telegram bot.");
      setStatus("Unable to start Telegram system manager mode.");
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5">
        <p className="text-xs uppercase tracking-[0.18em] text-slate-300">Telegram System Manager Mini App</p>
        <h1 className="text-xl font-semibold mt-2">Salon OS</h1>
        <p className="text-sm text-slate-200 mt-3">{status}</p>
        {error ? (
          <div className="mt-4 rounded-lg border border-rose-400/30 bg-rose-500/10 p-3 text-sm text-rose-100">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}
